import { useEffect, useState } from "react";
import { Box, Paper, Typography, Button, Grid, TextField, Tooltip } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import Loader from "../../../Components/Loader";
import { DateTime } from "luxon";
import { AmountFormat } from "../../../Components/CommonFunctions/CommonFunctions";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const NewDayEndReport = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [errorMsg, seterrorMsg] = useState(false);
  const [errorMsgText, seterrorMsgText] = useState('');
  const [successMsg, setsuccessMsg] = useState(false);
  const [successMsgText, setsuccessMsgText] = useState('');
  const [isloader, setisloader] = useState(false);
  const [AmountData, setAmountData] = useState(null);

  useEffect(() => {
    GetAmountData();
  }, []);

  const GetAmountData = (DateFromPicker = null) => {
    try {
      let SelectedDate = DateFromPicker ?
        DateTime.fromJSDate(new Date(DateFromPicker)).toFormat('yyyy-MM-dd') :
        (selectedDate ? DateTime.fromJSDate(new Date(selectedDate)).toFormat('yyyy-MM-dd') : "");
      setisloader(true);
      RestAPIService.getAll(Serviceurls.NEW_DAY_END_REPORT_CARD + `?date=${SelectedDate}`)
        .then((response) => {
          if (response.data.status === 'success') {
            setAmountData(response.data.data || null)
            setisloader(false);
          }
        }).catch(e => {
          if (e?.response?.data?.status === 'fail') {
            errorMessage(e.response.data.message)
          } else {
            errorMessage(e.message)
          }
        })
    } catch (e) {
      errorMessage(e.message)
    }
  }

  const GetPrint = () => {
    try {
      setisloader(true);
      RestAPIService.getAllPrint(Serviceurls.NEW_DAY_END_REPORT +
        `?date=${selectedDate ? DateTime.fromJSDate(new Date(selectedDate)).toFormat('yyyy-MM-dd') : ""}`)
        .then((response) => {
          const file = new Blob(
            [response.data],
            { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL);
          setisloader(false);
        }).catch((error) => {
          setisloader(false);
          errorMessage(error?.response?.data?.message || error?.message)
        })
    } catch (error) {
      setisloader(false);
      errorMessage(error.message)
    }
  }

  const errorMessage = (msg) => {
    setisloader(false)
    seterrorMsgText(msg)
    seterrorMsg(true)
  }
  const successMessage = (msg) => {
    setisloader(false)
    setsuccessMsgText(msg)
    setsuccessMsg(true)
  }

  const msgClose = () => {
    seterrorMsg(false);
    seterrorMsgText('');
    setsuccessMsg(false);
    setsuccessMsgText('');
  }

  const boxStyle = {
    p: 2,
    borderRadius: 1,
    textAlign: "center",
    minWidth: "12dvw", // responsive width
    height: "9dvh", // responsive height
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    boxShadow: "0px 1px 3px rgba(0,0,0,0.1)",
    backgroundColor: "#fff",
  };

  const labelStyle = {
    fontSize: "0.9dvw", // responsive label text
    color: "#555",
    fontWeight: 500,
  };

  const valueStyle = {
    fontSize: "1dvw", // responsive value text
    fontWeight: 600,
    marginTop: "0.4dvh",
  };

  return (
    <Box sx={{ p: "2dvw", backgroundColor: "#f6f8f7", minHeight: "78vh" }}>
      {/* Date Picker (top-right) */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: "1dvh", alignItems: 'center' }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={selectedDate ? selectedDate : null}
            name={"datePicker"}
            inputFormat={'DD-MM-YYYY'}
            views={["year", "month", "day"]}
            onChange={(newDate) => {
              setSelectedDate(newDate);
              GetAmountData(newDate);
            }}
            minDate={new Date("2025-09-10")}
            maxDate={new Date()}
            renderInput={(params) => <TextField
              {...params}
              label={"Date"}
              name={"Date"}
              variant="outlined"
              onKeyDown={(e) => e.preventDefault()}
              sx={{ width: '14dvw', marginRight: '0.3vw' }}
              size='small'
            />}
          />
        </LocalizationProvider>
        <Tooltip title="This report shows only records from 10-09-2025." placement="top" arrow>
          <ErrorOutlineIcon color="error" sx={{ marginLeft: "0.5vw" }} />
        </Tooltip>
      </Box>

      {/* Collection Statistics */}
      <Paper sx={{ p: "1.5dvw", mb: "2dvh" }}>
        <Typography sx={{ fontSize: "0.9dvw", fontWeight: 600, mb: "1dvh" }}>
          Collection Statistics
        </Typography>
        <Grid container spacing={2}>
          {[
            ["Total Collection Amount", AmountFormat(AmountData?.total_collection || 0)],
            ["Cash Amount", AmountFormat(AmountData?.total_cash || 0)],
            ["Card Amount", AmountFormat(AmountData?.total_card || 0)],
            ["UPI Amount", AmountFormat(AmountData?.total_upi || 0)],
            ["Bank Transfer Amount", AmountFormat(AmountData?.total_bank || 0)],
            ["Cheque Amount", AmountFormat(AmountData?.total_cheque || 0)],
          ].map(([label, value], index) => (
            <Grid item key={index}>
              <Paper sx={boxStyle}>
                <Typography sx={labelStyle}>{label}</Typography>
                <Typography sx={valueStyle}>{value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Bill Statistics */}
      <Paper sx={{ p: "1.5dvw", mb: "2dvh" }}>
        <Typography sx={{ fontSize: "0.9dvw", fontWeight: 600, mb: "1dvh" }}>
          Bill Statistics
        </Typography>
        <Grid container spacing={2}>
          {[
            ["Total Bill Amount", AmountFormat(AmountData?.total_bill_amount || 0)],
            ["Total Paid Amount", AmountFormat(AmountData?.total_received_amount || 0)],
            ["Total Credit Amount", AmountFormat(AmountData?.total_credit_amount || 0)],
            ["Net Advance Adjusted Amount", AmountFormat(AmountData?.net_advance_adjusted_amount || 0)],
          ].map(([label, value], index) => (
            <Grid item key={index}>
              <Paper sx={boxStyle}>
                <Typography sx={labelStyle}>{label}</Typography>
                <Typography sx={valueStyle}>{value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Download Section */}
      <Box>
        <Typography sx={{ fontSize: "0.9dvw", mb: "1dvh" }}>
          For Detailed Day End Report, Download this Print here
        </Typography>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={GetPrint}
          disabled={isloader}
          sx={{
            backgroundColor: "#00bfa5",
            "&:hover": { backgroundColor: "#009e8b" },
            fontSize: "0.8dvw",
            textTransform: "none",
            borderRadius: "4px",
            px: "1.5dvw",
          }}
        >
          Download
        </Button>
      </Box>
      {errorMsg ?
        <ToastMsg
          severity={'error'}
          msg={errorMsgText}
          msgPop={msgClose.bind(this)}
        />
        : null}
      {successMsg ?
        <ToastMsg
          severity={'success'}
          msg={successMsgText}
          msgPop={msgClose.bind(this)}
        />
        : null}
      {isloader ? <Loader loaderOpen={isloader} /> : null}
    </Box>
  );
};

export default NewDayEndReport;